/**
 * Bottom-up assessment of one KEV hit (DEV-100): component → product versions →
 * instances, each with its own VEX status.
 *
 * - The component verdict is only a proposal for the product versions that
 *   contain the component version.
 * - A confirmed product verdict AFFECTED creates the customer notifications of
 *   every instance running that version. Writing the Security Advisory and
 *   sending happen on the separate Benachrichtigungen page.
 * - An instance takes over the product verdict for the version it runs now and
 *   may deviate (environment reasons are allowed there), with environment
 *   factors and a remediation priority.
 *
 * The server owns every rule (422 with a German detail); the forms mirror them
 * only to say why saving is disabled.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Banner from '../../components/Banner';
import Chip, { ChipList } from '../../components/Chip';
import CveLink from '../../components/CveLink';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import Spinner, { PageSpinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  errorMessage,
  fetchEventAssessment,
  submitComponentAssessment,
  submitInstanceAssessment,
  submitProductAssessment,
} from '../../lib/api';
import { countLabel, fmtDate } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type {
  AssessmentPriority,
  AssessmentStatus,
  EventAssessmentDetail,
  InstanceAssessmentRow,
  ProductAssessmentRow,
  VexJustification,
  VulnerabilityEventRow,
} from '../../lib/types';

import { EventCveFacts, EventScopeFacts } from './EventFacts';
import {
  JUSTIFICATION_LABELS,
  PRIORITY_LABELS,
  PRIORITY_TONE,
  STATUS_LABELS,
  STATUS_TONE,
  komponenteLabel,
  verdictProblem,
} from './eventHelpers';

export type AssessmentTab = 'component' | 'products' | 'instances';

type Level = 'component' | 'product' | 'instance';

const STATUSES: Readonly<Record<Level, readonly AssessmentStatus[]>> = {
  component: ['UNDER_INVESTIGATION', 'AFFECTED', 'NOT_AFFECTED'],
  product: ['UNDER_INVESTIGATION', 'AFFECTED', 'NOT_AFFECTED', 'FIXED'],
  instance: ['UNDER_INVESTIGATION', 'AFFECTED', 'NOT_AFFECTED'],
};

const JUSTIFICATIONS = Object.keys(JUSTIFICATION_LABELS) as VexJustification[];

export interface AssessmentDialogProps {
  event: VulnerabilityEventRow;
  bewerter: string;
  canWrite: boolean;
  initialTab?: AssessmentTab;
  onClose: () => void;
  /** Any verdict was saved: the queue row has to be refetched. */
  onChanged: () => void;
}

function productLabel(product: ProductAssessmentRow): string {
  return [product.product_name, product.version_nr].filter(Boolean).join(' ') || `Produktversion #${product.version_id}`;
}

function StatusChip({ status, confirmed = true }: { status: AssessmentStatus; confirmed?: boolean }): ReactNode {
  return (
    <Chip tone={confirmed ? STATUS_TONE[status] : 'neutral'} title={confirmed ? undefined : 'Vorschlag, noch nicht bestätigt'}>
      {confirmed ? STATUS_LABELS[status] : `Vorschlag: ${STATUS_LABELS[status]}`}
    </Chip>
  );
}

/* ------------------------------------------------------------------------- *
 * Verdict form — shared by all three levels
 * ------------------------------------------------------------------------- */

export interface VerdictValues {
  status: AssessmentStatus;
  justification: VexJustification | null;
  comment: string;
}

interface VerdictFormProps {
  level: Level;
  /** Pre-selected: the stored verdict, otherwise the proposal. */
  initial: VerdictValues;
  proposal: AssessmentStatus;
  canWrite: boolean;
  saving: boolean;
  saveLabel: string;
  /** Extra fields (instance level) — part of the same form. */
  children?: ReactNode;
  /** The extra fields were changed (counts as unsaved). */
  extraDirty?: boolean;
  /** Saving without a change is meaningful: confirming a proposal or a review. */
  saveUnchanged?: boolean;
  onSave: (values: VerdictValues) => void;
  onDirtyChange: (dirty: boolean) => void;
}

function VerdictForm({
  level, initial, proposal, canWrite, saving, saveLabel, children, extraDirty = false, saveUnchanged = false,
  onSave, onDirtyChange,
}: VerdictFormProps): ReactNode {
  const [values, setValues] = useState<VerdictValues>(initial);
  const dirty = extraDirty || values.status !== initial.status
    || values.justification !== initial.justification || values.comment !== initial.comment;
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  const problem = verdictProblem(level, values.status, values.justification, values.comment, proposal);
  const justifications = level === 'instance' ? JUSTIFICATIONS : JUSTIFICATIONS.filter((j) => j !== 'ENVIRONMENT');
  const idBase = `verdict-${level}`;

  return (
    <form
      className="assessment-form"
      onSubmit={(formEvent) => {
        formEvent.preventDefault();
        if (problem === null && canWrite && !saving) onSave(values);
      }}
    >
      <fieldset disabled={!canWrite || saving}>
        <legend>Status</legend>
        <div className="seg assessment-status-choice" role="radiogroup" aria-label="Status">
          {STATUSES[level].map((status) => (
            <button
              key={status}
              type="button"
              role="radio"
              aria-checked={values.status === status}
              className={values.status === status ? 'on' : undefined}
              onClick={() => setValues((current) => ({
                ...current, status, justification: status === 'NOT_AFFECTED' ? current.justification : null,
              }))}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
        </div>
        {level !== 'component' && proposal !== 'OPEN' ? (
          <p className="hint">
            Vorschlag: <StatusChip status={proposal} />
            {values.status !== proposal && STATUSES[level].includes(proposal) ? (
              <button type="button" className="link" onClick={() => setValues((c) => ({ ...c, status: proposal }))}>
                übernehmen
              </button>
            ) : null}
          </p>
        ) : null}
        {values.status === 'NOT_AFFECTED' ? (
          <div className="field">
            <label htmlFor={`${idBase}-justification`}>VEX-Begründung *</label>
            <select
              id={`${idBase}-justification`}
              value={values.justification ?? ''}
              onChange={(e) => setValues((c) => ({ ...c, justification: (e.target.value || null) as VexJustification | null }))}
            >
              <option value="">— bitte wählen —</option>
              {justifications.map((j) => <option key={j} value={j}>{JUSTIFICATION_LABELS[j]}</option>)}
            </select>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor={`${idBase}-comment`}>
            {level === 'component' ? 'Erläuterung' : 'Begründung'}
            {values.status === 'NOT_AFFECTED' ? ' *' : ''}
          </label>
          <textarea
            id={`${idBase}-comment`}
            rows={3}
            value={values.comment}
            onChange={(e) => setValues((c) => ({ ...c, comment: e.target.value }))}
            placeholder={level === 'product'
              ? 'Pflicht bei „Nicht betroffen“ und bei Abweichung vom Vorschlag. Keine umgebungsbezogenen Gründe.'
              : level === 'instance'
                ? 'Pflicht bei „Nicht betroffen“ und bei Abweichung von der Produktbewertung.'
                : 'Pflicht bei „Nicht betroffen“.'}
          />
        </div>
        {children}
      </fieldset>
      <div className="assessment-form-actions">
        {problem && (dirty || saveUnchanged) ? <span className="hint" role="status">{problem}</span> : null}
        <div className="spacer" />
        <button
          type="submit"
          className="primary"
          disabled={!canWrite || saving || problem !== null || (!dirty && !saveUnchanged)}
          title={!canWrite ? missingPermissionTitle('triage.write') : problem ?? undefined}
        >
          {saving ? <Spinner variant="on-accent" /> : null}{saveLabel}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------- *
 * Dialog
 * ------------------------------------------------------------------------- */

export function AssessmentDialog({
  event, bewerter, canWrite, initialTab = 'component', onClose, onChanged,
}: AssessmentDialogProps): ReactNode {
  const toast = useToast();
  const [detail, setDetail] = useState<EventAssessmentDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tab, setTab] = useState<AssessmentTab>(initialTab);
  const [versionId, setVersionId] = useState<number | null>(null);
  const [instanceId, setInstanceId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirtyParts, setDirtyParts] = useState<ReadonlySet<string>>(new Set());
  const [confirmClose, setConfirmClose] = useState(false);
  // Remounts the forms with the server state after every save.
  const [revision, setRevision] = useState(0);

  const cve = event.cve.id ?? detail?.cve ?? '';

  const load = useCallback(async (): Promise<void> => {
    try {
      const fresh = await fetchEventAssessment(event.id);
      setDetail(fresh);
      setLoadError(null);
      setVersionId((current) => current ?? fresh.products[0]?.version_id ?? null);
    } catch (err) {
      setLoadError(errorMessage(err));
    }
  }, [event.id]);

  useEffect(() => { void load(); }, [load]);

  const markDirty = useCallback((part: string, dirty: boolean) => {
    setDirtyParts((current) => {
      if (current.has(part) === dirty) return current;
      const next = new Set(current);
      if (dirty) next.add(part); else next.delete(part);
      return next;
    });
  }, []);
  const dirtyHandlers = useMemo(() => ({
    component: (d: boolean) => markDirty('component', d),
    product: (d: boolean) => markDirty('product', d),
    instance: (d: boolean) => markDirty('instance', d),
  }), [markDirty]);
  const unsaved = canWrite && dirtyParts.size > 0;

  const instances = useMemo(
    () => (detail?.products ?? []).flatMap((p) => p.instances.map((i) => ({ instance: i, product: p }))),
    [detail],
  );
  const product = detail?.products.find((p) => p.version_id === versionId) ?? null;
  const selectedInstance = instances.find((row) => row.instance.instance_id === instanceId)
    ?? instances[0] ?? null;

  async function run(action: () => Promise<string>): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      const message = await action();
      toast.success(message);
      setDirtyParts(new Set());
      await load();
      setRevision((r) => r + 1);
      onChanged();
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  function requestClose(): void {
    if (saving) return;
    if (unsaved) setConfirmClose(true);
    else onClose();
  }

  const reviewMark = (needsReview: boolean, reason: string | null): ReactNode => needsReview ? (
    <Chip tone="warn" title={reason ?? undefined}>Erneut prüfen</Chip>
  ) : null;

  const tabs: Array<{ id: AssessmentTab; label: string; count?: number; attention?: boolean }> = [
    { id: 'component', label: 'Komponente' },
    {
      id: 'products', label: 'Produktversionen', count: detail?.products.length,
      attention: detail?.products.some((p) => p.needs_review || p.contradiction || !p.confirmed),
    },
    {
      id: 'instances', label: 'Instanzen', count: instances.length,
      attention: instances.some(({ instance }) => instance.needs_review || instance.version_changed),
    },
  ];

  return (
    <Modal
      title={<>Triage <span className="muted">/</span> <CveLink cve={event.cve.id} /></>}
      subtitle={`Event #${event.id} · ${komponenteLabel(event)}`}
      onClose={requestClose}
      size="wide"
      dismissable={!saving}
      footer={confirmClose ? (
        <div className="triage-close-confirm" role="alert">
          <div>
            <strong>Ungespeicherte Änderungen verwerfen?</strong>
            <p>Gespeicherte Bewertungen, Entwürfe und Anhänge bleiben erhalten.</p>
          </div>
          <button type="button" className="ghost" onClick={() => setConfirmClose(false)}>Weiter bearbeiten</button>
          <button type="button" className="danger" onClick={onClose}>Änderungen verwerfen</button>
        </div>
      ) : (
        <>
          <span className="triage-footer-note">
            {canWrite ? `Bewertet von ${bewerter}` : 'Nur lesend'}
          </span>
          <div className="spacer" />
          <button type="button" className="ghost" onClick={requestClose} disabled={saving}>Schließen</button>
        </>
      )}
    >
      <div className="triage-workspace" inert={confirmClose}>
        <div className="tabs triage-workspace-tabs" role="tablist" aria-label="Bewertungsebenen">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`assessment-tab-${item.id}`}
              aria-controls={`assessment-panel-${item.id}`}
              aria-selected={tab === item.id}
              tabIndex={tab === item.id ? 0 : -1}
              onClick={() => setTab(item.id)}
            >
              {item.label}
              {item.count !== undefined ? <Chip>{item.count}</Chip> : null}
              {item.attention ? <span className="triage-unsaved-dot" aria-label="Handlungsbedarf" /> : null}
            </button>
          ))}
        </div>

        {saveError ? (
          <Banner kind="error" sub="Nichts wurde gespeichert." onDismiss={() => setSaveError(null)}>{saveError}</Banner>
        ) : null}
        {loadError ? (
          <Banner kind="error" sub={<button type="button" className="ghost" onClick={() => void load()}>Erneut laden</button>}>
            Bewertung konnte nicht geladen werden: {loadError}
          </Banner>
        ) : null}
        {detail === null && loadError === null ? <PageSpinner text="Bewertung wird geladen…" /> : null}

        {detail !== null ? (
          <>
            {/* --- Component --------------------------------------------- */}
            <div role="tabpanel" id="assessment-panel-component" aria-labelledby="assessment-tab-component" hidden={tab !== 'component'}>
              <section className="triage-doc-panel">
                <div className="triage-panel-heading">
                  <h3>Komponentenbewertung</h3>
                  <StatusChip status={detail.component.status} />
                </div>
                <p className="hint">
                  Die Bewertung gilt für die Komponentenversion und ist ein Vorschlag für{' '}
                  {countLabel(detail.products.length, 'Produktversion', 'Produktversionen')}, die sie enthalten.
                  Benachrichtigt wird erst nach der Produktbewertung.
                </p>
                {detail.component.closed_by_scan ? (
                  <Banner kind="info">Vom KEV-Scan geschlossen: Die Version ist nicht mehr im Feld oder die CVE wird nicht mehr gemeldet.</Banner>
                ) : null}
                {detail.component.assessed_at ? (
                  <p className="hint">Zuletzt {fmtDate(detail.component.assessed_at)} · {detail.component.assessed_by ?? 'unbekannt'}</p>
                ) : null}
                <VerdictForm
                  key={`component-${revision}`}
                  level="component"
                  initial={{
                    status: detail.component.status === 'OPEN' ? 'UNDER_INVESTIGATION' : detail.component.status,
                    justification: detail.component.justification,
                    comment: detail.component.comment ?? '',
                  }}
                  proposal="OPEN"
                  saveUnchanged={detail.component.status === 'OPEN'}
                  canWrite={canWrite}
                  saving={saving}
                  saveLabel="Komponentenbewertung speichern"
                  onDirtyChange={dirtyHandlers.component}
                  onSave={(values) => void run(async () => {
                    const result = await submitComponentAssessment(event.id, values);
                    return `Komponentenbewertung gespeichert: ${STATUS_LABELS[result.status]}.`
                      + (result.marked_for_review ? ` ${countLabel(result.marked_for_review, 'Produktbewertung', 'Produktbewertungen')} zur erneuten Prüfung markiert.` : '');
                  })}
                />
                <details className="triage-context-details">
                  <summary>Schwachstelle & Umfang</summary>
                  <EventCveFacts event={event} />
                  <EventScopeFacts event={event} />
                </details>
              </section>
            </div>

            {/* --- Products ------------------------------------------------ */}
            <div role="tabpanel" id="assessment-panel-products" aria-labelledby="assessment-tab-products" hidden={tab !== 'products'}>
              {detail.products.length === 0 ? (
                <p className="muted">Keine Produktversion enthält diese Komponentenversion.</p>
              ) : (
                <>
                  <ul className="assessment-list" aria-label="Produktversionen">
                    {detail.products.map((p) => (
                      <li key={p.version_id}>
                        <button
                          type="button"
                          className={`assessment-list-item${p.version_id === versionId ? ' on' : ''}${p.effective_status === 'AFFECTED' && p.confirmed ? ' is-affected' : ''}`}
                          aria-pressed={p.version_id === versionId}
                          onClick={() => setVersionId(p.version_id)}
                        >
                          <span className="assessment-list-title">
                            <strong>{productLabel(p)}</strong>
                            {p.custom ? <Chip tone="info">Kundenversion</Chip> : null}
                          </span>
                          <ChipList>
                            <StatusChip status={p.effective_status} confirmed={p.confirmed} />
                            {reviewMark(p.needs_review, p.review_reason)}
                            {p.deviates ? <Chip title="Weicht vom Vorschlag der Komponentenbewertung ab">Abweichung</Chip> : null}
                            {p.contradiction ? <Chip tone="alert" title="Kunden wurden als betroffen benachrichtigt">Widerspruch</Chip> : null}
                          </ChipList>
                          <span className="hint">
                            {countLabel(p.instance_count, 'Instanz', 'Instanzen')}
                            {p.notifications.total ? ` · ${p.notifications.sent}/${p.notifications.total} benachrichtigt` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {product ? (
                    <section className="triage-doc-panel" aria-label={`Bewertung ${productLabel(product)}`}>
                      <div className="triage-panel-heading">
                        <h3>{productLabel(product)}</h3>
                        <StatusChip status={product.effective_status} confirmed={product.confirmed} />
                      </div>
                      {product.needs_review ? (
                        <Banner kind="warn" sub="Die bisherige Bewertung bleibt gültig, bis sie erneut gespeichert wird.">
                          Erneut prüfen: {product.review_reason ?? 'eine Komponentenbewertung hat sich geändert'}
                        </Banner>
                      ) : null}
                      {product.contradiction ? (
                        <Banner kind="error">
                          Kunden wurden als betroffen benachrichtigt, die Produktbewertung lautet jetzt „{STATUS_LABELS[product.effective_status]}“.
                          Die versendeten Benachrichtigungen bleiben bestehen.
                        </Banner>
                      ) : null}
                      {product.assessed_at ? (
                        <p className="hint">Zuletzt {fmtDate(product.assessed_at)} · {product.assessed_by ?? 'unbekannt'}</p>
                      ) : null}
                      <VerdictForm
                        key={`product-${product.version_id}-${revision}`}
                        level="product"
                        initial={{
                          status: product.status && product.status !== 'OPEN' ? product.status
                            : product.proposal === 'OPEN' ? 'UNDER_INVESTIGATION' : product.proposal,
                          justification: product.justification,
                          comment: product.comment ?? '',
                        }}
                        proposal={product.proposal}
                        canWrite={canWrite}
                        saving={saving}
                        saveLabel={product.confirmed ? 'Produktbewertung speichern' : 'Bestätigen'}
                        saveUnchanged={!product.confirmed || product.needs_review}
                        onDirtyChange={dirtyHandlers.product}
                        onSave={(values) => void run(async () => {
                          const result = await submitProductAssessment(product.version_id, cve, values);
                          const parts = [`Produktbewertung gespeichert: ${STATUS_LABELS[result.status]}.`];
                          if (result.notifications) {
                            parts.push(`${result.notifications.created} Benachrichtigung(en) angelegt, ${result.notifications.existing} vorhanden.`);
                          }
                          if (result.marked_for_review) {
                            parts.push(`${countLabel(result.marked_for_review, 'Instanzbewertung', 'Instanzbewertungen')} zur erneuten Prüfung markiert.`);
                          }
                          return parts.join(' ');
                        })}
                      />
                      {product.confirmed && product.effective_status === 'AFFECTED' ? (
                        <div className="assessment-form-actions">
                          <span className="hint">
                            {product.notifications.total
                              ? `${product.notifications.sent} von ${product.notifications.total} Benachrichtigungen versendet`
                              : 'Benachrichtigungen werden angelegt'}
                          </span>
                          <div className="spacer" />
                          <Link
                            className="ghost"
                            to={`/benachrichtigungen?version=${product.version_id}&cve=${encodeURIComponent(cve)}`}
                          >
                            <Icon name="mail" /> Zur Kundenbenachrichtigung
                          </Link>
                        </div>
                      ) : null}
                    </section>
                  ) : null}
                </>
              )}
            </div>

            {/* --- Instances ----------------------------------------------- */}
            <div role="tabpanel" id="assessment-panel-instances" aria-labelledby="assessment-tab-instances" hidden={tab !== 'instances'}>
              {instances.length === 0 ? (
                <p className="muted">Keine Produktinstanz läuft auf einer betroffenen Produktversion.</p>
              ) : (
                <>
                  <ul className="assessment-list" aria-label="Produktinstanzen">
                    {instances.map(({ instance, product: p }) => (
                      <li key={instance.instance_id}>
                        <button
                          type="button"
                          className={`assessment-list-item${instance.instance_id === selectedInstance?.instance.instance_id ? ' on' : ''}`}
                          aria-pressed={instance.instance_id === selectedInstance?.instance.instance_id}
                          onClick={() => setInstanceId(instance.instance_id)}
                        >
                          <span className="assessment-list-title">
                            <strong>S/N {instance.serial ?? instance.instance_id}</strong>
                            <span className="muted">{instance.customer ?? 'ohne Kunde'} · {productLabel(p)}</span>
                          </span>
                          <ChipList>
                            <StatusChip status={instance.effective_status} confirmed={instance.confirmed} />
                            {instance.priority ? <Chip tone={PRIORITY_TONE[instance.priority]}>Priorität {PRIORITY_LABELS[instance.priority]}</Chip> : null}
                            {reviewMark(instance.needs_review, instance.review_reason)}
                            {instance.version_changed ? <Chip tone="warn" title="Bewertet für eine andere Version als die aktuelle">Version gewechselt</Chip> : null}
                            {instance.deviates ? <Chip title="Weicht von der Produktbewertung ab">Abweichung</Chip> : null}
                          </ChipList>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {selectedInstance ? (
                    <InstancePanel
                      key={`instance-${selectedInstance.instance.instance_id}-${revision}`}
                      instance={selectedInstance.instance}
                      productName={productLabel(selectedInstance.product)}
                      canWrite={canWrite}
                      saving={saving}
                      onDirtyChange={dirtyHandlers.instance}
                      onSave={(values, extra) => void run(async () => {
                        const result = await submitInstanceAssessment(selectedInstance.instance.instance_id, cve, { ...values, ...extra });
                        return `Instanzbewertung gespeichert: ${STATUS_LABELS[result.status]}.`;
                      })}
                    />
                  ) : null}
                </>
              )}
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------------- *
 * Instance panel: verdict plus environment factors and priority
 * ------------------------------------------------------------------------- */

interface InstanceExtra {
  priority: AssessmentPriority | null;
  network_connected: boolean;
  remote_access: boolean;
  compensating_controls: boolean;
  environment_notes: string;
}

function InstancePanel({ instance, productName, canWrite, saving, onDirtyChange, onSave }: {
  instance: InstanceAssessmentRow;
  productName: string;
  canWrite: boolean;
  saving: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onSave: (values: VerdictValues, extra: InstanceExtra) => void;
}): ReactNode {
  const initialExtra: InstanceExtra = {
    priority: instance.priority,
    network_connected: instance.network_connected,
    remote_access: instance.remote_access,
    compensating_controls: instance.compensating_controls,
    environment_notes: instance.environment_notes ?? '',
  };
  const [extra, setExtra] = useState<InstanceExtra>(initialExtra);
  const extraDirty = (Object.keys(initialExtra) as (keyof InstanceExtra)[])
    .some((key) => extra[key] !== initialExtra[key]);
  const factors: Array<{ key: 'network_connected' | 'remote_access' | 'compensating_controls'; label: string }> = [
    { key: 'network_connected', label: 'Netzanbindung' },
    { key: 'remote_access', label: 'Fernzugang' },
    { key: 'compensating_controls', label: 'Kompensationsmaßnahmen vorhanden' },
  ];

  return (
    <section className="triage-doc-panel" aria-label={`Bewertung S/N ${instance.serial ?? instance.instance_id}`}>
      <div className="triage-panel-heading">
        <h3>S/N {instance.serial ?? instance.instance_id} <span className="muted">· {instance.customer ?? 'ohne Kunde'} · {productName}</span></h3>
        <StatusChip status={instance.effective_status} confirmed={instance.confirmed} />
      </div>
      {instance.needs_review ? (
        <Banner kind="warn" sub="Die bisherige Bewertung bleibt gültig, bis sie erneut gespeichert wird.">
          Erneut prüfen: {instance.review_reason ?? 'die Produktbewertung hat sich geändert'}
        </Banner>
      ) : null}
      {instance.version_changed ? (
        <Banner kind="warn">Die Instanz läuft inzwischen auf einer anderen Version; die frühere Bewertung gilt nur als Vorschlag.</Banner>
      ) : null}
      <p className="hint">Der Kunde wird über eine betroffene Produktversion in jedem Fall informiert; die Instanzbewertung dient der Priorisierung.</p>
      <VerdictForm
        level="instance"
        initial={{
          status: instance.status && instance.status !== 'OPEN' ? instance.status
            : instance.proposal === 'OPEN' || instance.proposal === 'FIXED' ? 'UNDER_INVESTIGATION' : instance.proposal,
          justification: instance.justification,
          comment: instance.comment ?? '',
        }}
        proposal={instance.proposal}
        canWrite={canWrite}
        saving={saving}
        saveLabel={instance.confirmed ? 'Instanzbewertung speichern' : 'Bestätigen'}
        extraDirty={extraDirty}
        saveUnchanged={!instance.confirmed || instance.needs_review}
        onDirtyChange={onDirtyChange}
        onSave={(values) => onSave(values, extra)}
      >
        <div className="assessment-environment">
          <span className="assessment-environment-label">Umgebungsfaktoren</span>
          {factors.map((factor) => (
            <label key={factor.key} className="check">
              <input
                type="checkbox"
                checked={extra[factor.key]}
                onChange={(e) => setExtra((c) => ({ ...c, [factor.key]: e.target.checked }))}
              />
              {factor.label}
            </label>
          ))}
        </div>
        <div className="field">
          <label htmlFor="instance-environment-notes">Anmerkungen zur Einsatzumgebung</label>
          <textarea
            id="instance-environment-notes"
            rows={2}
            value={extra.environment_notes}
            onChange={(e) => setExtra((c) => ({ ...c, environment_notes: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="instance-priority">Priorität der Behebung</label>
          <select
            id="instance-priority"
            value={extra.priority ?? ''}
            onChange={(e) => setExtra((c) => ({ ...c, priority: (e.target.value || null) as AssessmentPriority | null }))}
          >
            <option value="">— keine —</option>
            {(Object.keys(PRIORITY_LABELS) as AssessmentPriority[]).map((p) => (
              <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
            ))}
          </select>
        </div>
      </VerdictForm>
    </section>
  );
}

export default AssessmentDialog;
